<script setup lang="ts">
import AppFormBuilder from "@/components/UI/AppFormBuilder.vue";
import genders from "@/constants/genders.js";
import AuthApi from "@/api/auth.api.ts";
import AppInput from "@/components/UI/AppInput/AppInput.vue";
import MguuInput from "@/components/UI/MguuInput.vue";
import { format } from "date-fns";
import { FieldsType, rules, useValidate } from "@/services/validate.ts";
import { orderBy } from "lodash";
import { useRoute } from "vue-router";
import { vi } from "date-fns/locale";
import { useStore } from "vuex";

interface Props {
  handler: (payload) => Promise<void>;
  handlerButtonText?: string;
  disableButton?: boolean;
  half?: boolean;
  user?: any;
  sort?: object;
  forceMguu?: boolean;
  moreFields?: {
    [key: string]: any;
  };
}

const route = useRoute();
const store = useStore();
const slots = useSlots();
const { validate, errors } = useValidate();

const isMguuRespondent = computed(() => {
  if (props.forceMguu) return import.meta.env.VITE_APP_DESIGN_TYPE === 'mguu';
  return import.meta.env.VITE_APP_DESIGN_TYPE === 'mguu' && store.getters.authUserRoleSlug === 'respondent';
});

const props = defineProps<Props>();

const emits = defineEmits(["change", "validation-error"]);

const appFormBuilderFields = ref([]);
const fields = reactive({
  lastname: {
    type: "string",
    required: true,
    modelValue: props.user?.lastname ?? null,
    key: "lastname",
    bind: {
      label: "Фамилия *",
      half: props.half
    },
    rules: {
      required: rules.required
    }
  },
  firstname: {
    type: "string",
    modelValue: props.user?.firstname ?? null,
    key: "firstname",
    bind: {
      label: "Имя *",
      half: props.half
    },
    rules: {
      required: rules.required
    }
  },
  middlename: {
    type: "string",
    modelValue: props.user?.middlename ?? null,
    key: "middlename",
    bind: {
      label: "Отчество",
      half: props.half
    }
  },

  birthday: {
    modelValue: props.user?.birthday ?? null,
    bind: {
      label: "Дата рождения",
      half: props.half,
      maxDate: format(new Date(), "dd.MM.yyyy")
    },
    type: "date",
    key: "birthday",
    value: null,
    rules: {
      required: rules.required
    }
  },
  gender: {
    name: "Пол",
    type: "radio",
    key: "gender",
    modelValue: props.user?.gender ?? null,
    bind: {
      label: "Пол",
      half: props.half,
      items: genders.map(({ slug, name }) => ({ name, uuid: slug })),
      itemText: "name",
      itemValue: "uuid"
    }
  }
});

if (props.moreFields) {
  Object.keys(props.moreFields).forEach(key => {
    fields[key] = props.moreFields[key];
  });
}

async function getAdditionalFormFields() {
  const { data } = await AuthApi.getUserAdditionalForm({ formable_type: "null", formable_id: "null" });

  data.data.forEach(field => {
    const foundItem = props.user?.formSettings.find(item => item.uuid === field.uuid);

    fields[field.name] = {
      modelValue: foundItem ? foundItem.value : "",
      key: field.name,
      ...field,
      groupType: "additionalFields",
      bind: {
        label: `${field.name} ${field.required ? "*" : ""}`,
        half: props.half,
        items: field.variants,
        itemText: "name",
        itemValue: "uuid"
      }
    };

    if (field.required) {
      fields[field.name].rules = {
        required: rules.required
      };
    }
  });

  appFormBuilderFields.value = sortFields();
}

async function getPlanFormFields() {
  const { data } = await AuthApi.getUserAdditionalForm({ formable_type: "plan", formable_user_uuid: props.user?.uuid });

  data.data.forEach(field => {
    const foundItem = props.user?.formSettings.find(item => item.uuid === field.uuid);

    if (typeof route.query.plannofilled === "string" && route.query.plannofilled !== field.plan.uuid) {
      field.required = false;
    }

    let modelValue = foundItem ? foundItem.value : "";

    fields[field.uuid] = {
      modelValue: modelValue,
      key: field.name,
      ...field,
      groupName: field.plan.name,
      groupUuid: field.plan.uuid,
      groupType: "planFields",
      bind: {
        label: `${field.name} ${field.required ? "*" : ""}`,
        half: props.half,
        items: field.variants,
        itemText: "name",
        itemValue: "uuid"
      }
    };

    if (field.required) {
      fields[field.uuid].rules = {
        required: rules.required
      };
    }
  });

  appFormBuilderFields.value = sortFields();
}

getAdditionalFormFields().then(() => {
  validateFields();
});
getPlanFormFields().then(() => {
  validateFields();
});

async function submit(data) {
  const baseFields = data[0].fields;
  const planFields = data.slice(1)?.map(item => item.fields).flat();

  const fields = {};
  baseFields.forEach(field => {
    fields[field.key] = field;
  });
  const additionalFields = [...baseFields, ...planFields].filter((field) => field.groupType === "additionalFields" || field.groupType === "planFields");

  return await props.handler({
    firstname: fields.firstname?.modelValue,
    lastname: fields.lastname?.modelValue,
    middlename: fields.middlename?.modelValue,
    birthday: fields.birthday?.modelValue,
    gender: fields.gender?.modelValue,
    role: fields.role?.modelValue,
    email: fields.email?.modelValue,
    formSettings: additionalFields.map(field => ({
      uuid: field.uuid,
      value: field.modelValue,
      type: field.type
    }))
  });
}

defineExpose({
  fields: fields
});

watch(fields, () => {
  emits("change", fields);
});

const validateFields = () => {
  validate(fields as FieldsType).then(() => {

    for (const errorsKey of Object.keys(errors.value)) {
      if (fields[errorsKey]) {
        fields[errorsKey].errors = errors.value[errorsKey];
      }
    }
  });
};

const sortFields = () => {
  if (typeof props.sort == "undefined") {
    return fields;
  }
  Object.keys(fields).forEach(key => {
    fields[key]["order"] = 0;
  });
  Object.keys(props.sort).forEach(slug => {
    let position = props.sort[slug] ?? 0;
    if (typeof fields[slug] != "undefined") {
      fields[slug]["order"] = position;
    }
  });

  return orderBy(Object.values(fields), "order", "desc");
};

appFormBuilderFields.value = sortFields();
</script>

<template>
  <app-form-builder
    v-model="appFormBuilderFields"
    :handler="submit"
    :handler-button-text="handlerButtonText"
    :disable-button="disableButton"
    :force-mguu="forceMguu"
    @validation-error="emits('validation-error', $event)"
  >
    <template #prepend-form>
      <MguuInput
        v-if="user?.email && isMguuRespondent"
        label="E-mail"
        type="email"
        :model-value="user.email"
        disabled
      />
      <app-input
        v-else-if="user?.email"
        :label="app_design_type === 'mguu' ? 'E-mail' : 'E-mail *'"
        type="email"
        :value="user.email"
        :disabled="true"
      />
    </template>
    <template #footer-left v-if="isMguuRespondent && slots['footer-left']">
      <slot name="footer-left" />
    </template>
    <template #prepend-buttons>
      <slot name="prepend-buttons" />
    </template>
    <template #append-buttons>
      <slot name="append-buttons" />
    </template>
  </app-form-builder>
</template>
